import { useQuery } from "@tanstack/react-query";
import { SnackbarKey, useSnackbar } from "notistack";
// Material
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Dialog, DialogContent, DialogTitle, IconButton, Skeleton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
// Components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
// API
import { getPayrollSubmit } from "@/Washouts/api/PayrollClosing/queries";
import { PayrollPreviewTable } from "../../tables/PayrollClosing/PayrollPreviewTable";
import { styled } from "@mui/material/styles";

type CommissionCyclePayrollSubmissionHistoryDialogProps = {
	locationId: string;
	previewId: string;
	openDialog: boolean;
	setOpenDialog: (value: boolean) => void;
};

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

export default function CommissionCyclePayrollSubmissionHistoryDialog(props: Readonly<CommissionCyclePayrollSubmissionHistoryDialogProps>) {
	const { locationId, previewId, openDialog, setOpenDialog } = props;

	const { enqueueSnackbar } = useSnackbar();

	const { data: previewData, isLoading: waiting } = useQuery({
		queryKey: ["commissionCycleClosingPayrollSubmit", previewId, locationId],
		queryFn: async () => {
			try {
				return await getPayrollSubmit(locationId, previewId);
			} catch {
				const renderSnackbarsActions = (key: SnackbarKey) => {
					return <SnackbarsActions key={key.toString()} index={key} />;
				};
				enqueueSnackbar(<SnackbarsContent message={"An error occurred retrieving the requested payroll submission."} critical={true} />, {
					variant: "error",
					preventDuplicate: true,
					action: renderSnackbarsActions,
				});
			}
			return null;
		},
		enabled: !!locationId && !!previewId,
		staleTime: Infinity,
	});

	const handleClose = () => {
		setOpenDialog(false);
	};

	return (
		<BootstrapDialog open={openDialog} onClose={handleClose} className="payroll-preview-dialog" maxWidth={false}>
			<DialogTitle>
				<Paper elevation={0} sx={{ width: "1155px", display: waiting ? "block" : "none" }}>
					<Grid container direction="row" justifyContent="space-between" alignItems="center">
						<Grid item>
							<Skeleton
								sx={{
									borderRadius: "6px",
								}}
								variant="rectangular"
								width={220}
								height={30}
							/>
						</Grid>
						<Grid item>
							<Skeleton variant="circular" width={30} height={30} />
						</Grid>
					</Grid>
				</Paper>
				{!waiting && (
					<>
						<Typography className="payroll-preview-dialog-title" component="div">
							Submission Details
						</Typography>
						<IconButton aria-label="close" size="medium" className="payroll-preview-close-btn" onClick={handleClose}>
							<CloseRoundedIcon fontSize="inherit" />
						</IconButton>
					</>
				)}
			</DialogTitle>
			<DialogContent sx={{ width: "1200px" }}>
				<Paper elevation={0} sx={{ width: "1150px", display: waiting ? "block" : "none" }}>
					<TableLoader
						sx={{
							marginTop: "-10px !important",
						}}
						content
						rows={3}
					/>
				</Paper>
				{!waiting && <PayrollPreviewTable data={previewData} />}
			</DialogContent>
		</BootstrapDialog>
	);
}
